import { DataGridOO } from "@mlpa/core-ui-core-open_ocean_ui/Organisms/Tables/DataGridOO";
import { GridColDef } from "@mui/x-data-grid-pro";
import { EntityMappings } from "graphqlBase/__utils__/entityMappings";
import debounce from "lodash/debounce";
import * as React from "react";
import { useRecoilState } from "recoil";
import { makeGridPaginationState, makeGridState, TableStateExtended, useRegisterGridApiRef } from "../gridStatePersist";
import { BaseRow, DataGridFactoryParams, DataGridOOWrapperProps } from "../types";

export function DataGridOOFactory<R extends BaseRow, E extends keyof EntityMappings>({
  apiRef,
  executeQuery,
  // @ts-ignore
  DataGridComponent = DataGridOO,
  tableMode = "server",
  persistance,
  tableId,
  persistDelay = 1000,
  ...props
}: DataGridFactoryParams) {
  const DataGridOOWrapper: React.FC<DataGridOOWrapperProps<R, E>> = ({
    rows,
    columns,
    toolbarProps,
    showToolbar = true,
    totalCount,
    ...props
  }) => {
    const serverSideLogic = tableMode === "server";
    const tableState = makeGridState(tableId, persistance);
    const [persistedTableState, setTableState] = useRecoilState(tableState);
    useRegisterGridApiRef(tableId, apiRef);
    const tablePaginationState = makeGridPaginationState({
      tableId,
      persistType: "session",
      initState: props?.initialState?.pagination?.paginationModel,
    });
    const [paginationModel, setPaginationModel] = useRecoilState(tablePaginationState);

    const pagination = props.pagination ?? true;
    // must be in a ref for pagination to work correctly with server-side data,
    // as the grid relies on the rowCount to determine when to call for more data
    const rowCountRef = React.useRef(totalCount ?? 0);

    const rowCount = React.useMemo(() => {
      // During server-side refetches (e.g. pagination), Apollo temporarily reports totalCount as 0
      // before the new page arrives. If we pass 0 to the grid while on page > 0, MUI treats the
      // current page as out of bounds and resets it to 0, triggering a second, wrong query.
      if (totalCount !== undefined && (totalCount > 0 || paginationModel.page === 0)) {
        rowCountRef.current = totalCount;
      }
      return rowCountRef.current;
    }, [totalCount, paginationModel.page]);

    const persistState = React.useMemo(
      () =>
        debounce((updates: Partial<TableStateExtended>) => {
          if (persistance) {
            setTableState((prevState) => ({
              ...prevState,
              ...updates,
            }));
          }
        }, persistDelay),
      [persistance, setTableState, persistDelay]
    );

    const initState = persistedTableState ?? props?.initialState;

    // Apply persisted column widths to columns to maintain widths across refetches
    const columnsWithPersistedWidths = React.useMemo(() => {
      if (!persistedTableState?.columns?.dimensions) return columns;

      return columns.map((col) => {
        const persistedWidth = persistedTableState?.columns?.dimensions?.[col.field]?.width;
        // Only apply persisted width and remove flex if the column has been resized
        if (persistedWidth !== undefined) {
          return { ...col, width: persistedWidth, flex: undefined };
        }
        // Keep original flex if no persisted width (column hasn't been resized yet)
        return col;
      });
    }, [columns, persistedTableState?.columns?.dimensions, tableId]);

    return (
      <DataGridComponent
        {...props}
        initialState={initState}
        apiRef={apiRef}
        processRowUpdate={(newV, oldV) => {
          return newV;
        }}
        showToolbar={showToolbar}
        filterMode={tableMode}
        sortingMode={tableMode}
        paginationMode={tableMode}
        onColumnVisibilityModelChange={(model, details) => {
          if (props.onColumnVisibilityModelChange) props.onColumnVisibilityModelChange(model, details);
          persistState({
            columns: {
              columnVisibilityModel: model,
            },
          });
        }}
        onColumnWidthChange={(params, event, details) => {
          if (props.onColumnWidthChange) props.onColumnWidthChange(params, event, details);
          persistState({
            columns: {
              dimensions: {
                ...persistedTableState?.columns?.dimensions,
                [params.colDef.field]: { width: params.width },
              },
            },
          });
        }}
        onFilterModelChange={(filterModel, details) => {
          if (props.onFilterModelChange) props.onFilterModelChange(filterModel, details);
          persistState({
            filter: {
              filterModel,
            },
          });
          setTimeout(() => {
            // ensure grid state is updated before query execution
            if (serverSideLogic) executeQuery();
          }, 1);
        }}
        density={props.density}
        onSortModelChange={(model, details) => {
          if (props.onSortModelChange) props.onSortModelChange(model, details);
          persistState({
            sorting: {
              sortModel: model,
            },
          });

          setTimeout(() => {
            // ensure grid state is updated before query execution
            if (serverSideLogic) executeQuery();
          }, 1);
        }}
        onPaginationModelChange={(paginationModel, details) => {
          if (props.onPaginationModelChange) props.onPaginationModelChange(paginationModel, details);
          setPaginationModel(paginationModel);

          setTimeout(() => {
            // ensure grid state is updated before query execution
            if (serverSideLogic) executeQuery();
          }, 1);
        }}
        paginationModel={paginationModel}
        pagination={pagination}
        rowCount={rowCount}
        rows={rows ?? []}
        columns={columnsWithPersistedWidths as GridColDef[]}
        keepNonExistentRowsSelected
      />
    );
  };

  return DataGridOOWrapper;
}
